/**
 * Settings → Organisation → Currencies offered (DEV-162): the ordered list of
 * ISO 4217 currencies the platform opens accounts in. It replaces the lists
 * that were typed into core-banking (NB_SUPPORTED_CURRENCIES), the account
 * opening dialog, the customer fee tab, the Treasury currency maps and the
 * provider costs. One definition for both sides: the back office checks the
 * form with it and shows what a save changes; the API checks the same rules
 * before storing and builds the History from the same change list.
 *
 * The order is the order every picker shows.
 */

import { z } from 'zod';

import { isIso4217Currency } from '../currency/iso4217.js';
import type { SettingChange } from '../settings-diff/index.js';

/** The namespace of kyc.settings_versions this setting is stored under. */
export const CURRENCIES_NAMESPACE = 'currencies';

export interface CurrenciesOfferedSettings {
  /** ISO 4217 codes, upper case, no duplicates, at least one, in display order. */
  readonly currencies: readonly string[];
}

/**
 * What every instance starts with (decided 24.10, DEV-162): the seven
 * currencies the code listed until then, plus CAD, in that order. Stored once
 * as version 1 when nothing is stored (the API's boot seed); after that only
 * an admin changes it.
 */
export const CURRENCIES_OFFERED_SEED: CurrenciesOfferedSettings = {
  currencies: ['EUR', 'GBP', 'USD', 'CHF', 'SEK', 'NOK', 'DKK', 'CAD'],
};

/** The most the list holds: more than any picker shows usefully. */
export const MAX_OFFERED_CURRENCIES = 40;

export type CurrenciesOfferedValidation =
  | { readonly ok: true; readonly value: CurrenciesOfferedSettings }
  | { readonly ok: false; readonly error: string };

/** Trim, upper-case and check a submitted list. Never throws. */
export function validateCurrenciesOffered(input: unknown): CurrenciesOfferedValidation {
  const raw = typeof input === 'object' && input !== null ? (input as Record<string, unknown>)['currencies'] : undefined;
  if (!Array.isArray(raw)) return { ok: false, error: 'List the currencies offered.' };
  if (raw.some((code) => typeof code !== 'string')) return { ok: false, error: 'Each currency is a three-letter ISO 4217 code.' };
  const codes = (raw as string[]).map((code) => code.trim().toUpperCase());
  if (codes.length === 0) return { ok: false, error: 'Offer at least one currency.' };
  if (codes.length > MAX_OFFERED_CURRENCIES) return { ok: false, error: `Offer at most ${String(MAX_OFFERED_CURRENCIES)} currencies.` };
  const unknown = codes.filter((code) => !isIso4217Currency(code));
  if (unknown.length > 0) {
    const names = unknown.map((c) => (c === '' ? '(empty)' : c)).join(', ');
    return { ok: false, error: unknown.length === 1 ? `${names} is not an active ISO 4217 currency code.` : `${names} are not active ISO 4217 currency codes.` };
  }
  const duplicates = [...new Set(codes.filter((code, index) => codes.indexOf(code) !== index))];
  if (duplicates.length > 0) return { ok: false, error: `${duplicates.join(', ')} ${duplicates.length === 1 ? 'is' : 'are'} listed more than once.` };
  return { ok: true, value: { currencies: codes } };
}

/**
 * The stored shape of the `currencies` namespace: exactly `currencies`, and
 * the list must pass validateCurrenciesOffered unchanged (already upper case
 * and trimmed), so what is stored is what every reader acts on.
 */
export const currenciesOfferedSchema = z.object({
  currencies: z.array(z.string()),
}).strict().superRefine((value, ctx) => {
  const checked = validateCurrenciesOffered(value);
  if (!checked.ok) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currencies'], message: checked.error });
    return;
  }
  if (checked.value.currencies.some((code, i) => code !== value.currencies[i])) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currencies'], message: 'Codes are stored upper case, without spaces.' });
  }
});

export interface OfferedCurrencyDiff {
  readonly added: readonly string[];
  readonly removed: readonly string[];
  /** True when the codes kept are in a different order. */
  readonly reordered: boolean;
}

export function offeredCurrencyDiff(before: CurrenciesOfferedSettings | null, after: CurrenciesOfferedSettings): OfferedCurrencyDiff {
  const old = before?.currencies ?? [];
  const added = after.currencies.filter((code) => !old.includes(code));
  const removed = old.filter((code) => !after.currencies.includes(code));
  const keptBefore = old.filter((code) => after.currencies.includes(code));
  const keptAfter = after.currencies.filter((code) => old.includes(code));
  return { added, removed, reordered: keptBefore.some((code, i) => code !== keptAfter[i]) };
}

const listed = (codes: readonly string[]): string => (codes.length === 0 ? '—' : codes.join(', '));

/**
 * What a save changes, for the save bar and the History: the list as a whole
 * (its order is part of it), then what was added and what was removed.
 * `before` null: nothing was stored yet.
 */
export function currenciesOfferedChanges(before: CurrenciesOfferedSettings | null, after: CurrenciesOfferedSettings): SettingChange[] {
  const diff = offeredCurrencyDiff(before, after);
  if (before !== null && diff.added.length === 0 && diff.removed.length === 0 && !diff.reordered) return [];
  return [
    { key: 'currencies', label: 'Currencies offered', before: before === null ? null : listed(before.currencies), after: listed(after.currencies) },
    ...(before !== null && diff.added.length > 0 ? [{ key: 'currencies.added', label: 'Added', before: null, after: diff.added.join(', ') }] : []),
    ...(diff.removed.length > 0 ? [{ key: 'currencies.removed', label: 'Removed', before: diff.removed.join(', '), after: null }] : []),
  ];
}

/**
 * Codes to show in a picker: the offered ones in their order, then any code a
 * record already uses that is no longer offered (an existing account, a fee,
 * a Treasury account), so nothing on file disappears from the screen.
 */
export function withCurrenciesInUse(offered: readonly string[], inUse: Iterable<string>): string[] {
  const extra = [...new Set([...inUse].map((c) => c.toUpperCase()))].filter((c) => c !== '' && !offered.includes(c)).sort();
  return [...offered, ...extra];
}
